'use client'

import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Select as SelectPrimitive } from 'radix-ui'
import type * as React from 'react'

import { useDocumentDirection } from '../../hooks/use-document-direction'
import { fieldTriggerClass, quietFieldTrigger } from '../../lib/field-trigger'
import { cn } from '../../lib/utils'

function Select({ dir: providedDir, ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  return <SelectPrimitive.Root data-slot="select" dir={dir} {...props} />
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

/**
 * The field skin (`lib/field-trigger.ts`). `appearance="quiet"` is the quiet
 * select of DESIGN.md §5: text and a chevron whose boundary appears on hover
 * and focus, for rows of controls that read as information until worked on.
 */
function SelectTrigger({
  className,
  size = 'default',
  appearance = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default'
  appearance?: 'default' | 'quiet'
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-appearance={appearance}
      className={fieldTriggerClass(
        '[&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:truncate',
        size === 'sm' && 'h-control-compact',
        appearance === 'quiet' && quietFieldTrigger,
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  // A list below the trigger with one fixed scroll area, like any web
  // dropdown. Radix's `item-aligned` grows the list on every scroll to an
  // edge until it stops scrolling (b592), and loses its place without a
  // `<SelectValue>`. `align` only applies to popper: start, not centred.
  position = 'popper',
  align = 'start',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          // The popover elevation (DESIGN.md §4): a 1px `--border` edge and
          // the popover shadow on overlay paper, 6px corners.
          'relative z-50 max-h-(--radix-select-content-available-height) min-w-32 overflow-x-hidden overflow-y-auto',
          'rounded-md border border-border bg-popover text-popover-foreground shadow-popover',
          'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          // Never wider than the room beside the trigger: a long option wraps
          // instead of running off a 375px screen.
          position === 'popper' &&
            'max-w-(--radix-select-content-available-width) data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-small text-muted-foreground', className)}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex min-h-9 w-full cursor-default items-center gap-2 rounded-sm py-2 pe-8 ps-2.5 text-body outline-hidden select-none',
        'focus:bg-accent focus:text-accent-foreground',
        // The chosen option is grey ink plus a tick (The Shape-as-Well-as-
        // Colour Rule), and keeps its fill under the pointer: a hover is
        // never a selection, and a selection never looks like a hover.
        'data-[state=checked]:bg-brand-subtle data-[state=checked]:text-brand-subtle-foreground',
        'data-[state=checked]:focus:bg-brand-subtle data-[state=checked]:focus:text-brand-subtle-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:text-muted-foreground',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute end-2 flex size-4 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('pointer-events-none -mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
